import { useState } from 'react';
import { dilute } from '../../shared/dilution.js';

// Cold Brew Dilution — Knowledge → Tools. Hit a target TDS on the first pour:
// add water to concentrate on hand, or pull concentrate for a target batch.
// Calc lives in shared/dilution.js (one pure function, reused by tests + future
// server-side logging). Client-only; no API. Mobile-first for the prep station.

const FOREST = '#1f4d3a';
const AMBER = '#b06f26';

const r1 = (n) => (Math.round(n * 10) / 10).toFixed(1);
const galText = (oz) => (oz >= 128 ? ` (${(oz / 128).toFixed(2).replace(/\.?0+$/, '')} gal)` : '');

function Field({ label, value, onChange, unit, placeholder, onEnter }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#6f6a5e', marginBottom: 6 }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type="number" inputMode="decimal" step="any" value={value} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onEnter(); } }}
          style={{ width: '100%', minHeight: 48, padding: '12px 44px 12px 14px', fontSize: 17,
                    color: '#1c1a15', background: '#fbfaf6', border: '1.5px solid #e6e2d8', borderRadius: 12,
                    fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit' }} />
        <span style={{ position: 'absolute', right: 14, top: 14, color: '#9e9a8e', fontSize: 14, fontWeight: 600, pointerEvents: 'none' }}>{unit}</span>
      </div>
    </div>
  );
}

function Chip({ children, onClick, preset }) {
  return (
    <button type="button" onClick={onClick}
      style={{ minHeight: 40, padding: '6px 13px', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
                color: preset ? '#12341f' : '#1c1a15', background: preset ? 'rgba(31,77,58,0.12)' : '#fbfaf6',
                border: preset ? '1.5px solid transparent' : '1.5px solid #e6e2d8', borderRadius: 999 }}>
      {children}
    </button>
  );
}

function Result({ side, res, copied, onCopy }) {
  if (!res) return null;
  if (!res.ok) {
    return (
      <div role="alert" style={{ marginTop: 16, padding: '14px 16px', color: '#b23b2e', fontSize: 14, fontWeight: 600,
                                  background: 'rgba(178,59,46,0.08)', border: '1px solid rgba(178,59,46,0.3)', borderRadius: 12 }}>
        {res.error}
      </div>
    );
  }
  const isWater = res.mode === 'water';
  const big = isWater ? `${r1(res.water)} oz` : `${r1(res.concentrate)} oz`;
  const cap = isWater ? 'of water to add' : 'of concentrate to pull';
  const gal = galText(res.finalVolume);
  const say = isWater
    ? `Dilute with ${r1(res.water)} oz of water → ${r1(res.finalVolume)} oz${gal} of cold brew at ${res.targetTDS}% TDS.`
    : `For ${r1(res.finalVolume)} oz${gal} at ${res.targetTDS}% TDS, use ${r1(res.concentrate)} oz of concentrate and ${r1(res.water)} oz of water.`;
  const rows = [
    ['Concentrate', `${r1(res.concentrate)} oz @ ${res.concentrateTDS}%`],
    ['Water', `${r1(res.water)} oz`],
    ['Final', `${r1(res.finalVolume)} oz${gal} @ ${res.targetTDS}%`],
  ];
  return (
    <div style={{ marginTop: 16, border: '1px solid #e6e2d8', borderRadius: 14, overflow: 'hidden' }} aria-live="polite">
      <div style={{ background: '#f3e7d4', padding: '16px', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 30, fontWeight: 800, color: AMBER, letterSpacing: '-0.02em' }}>{big}</span>
        <span style={{ fontSize: 15, fontWeight: 600, color: '#1c1a15' }}>{cap}</span>
      </div>
      <div style={{ padding: '12px 16px', fontSize: 15, fontWeight: 600, color: '#12341f', background: 'rgba(31,77,58,0.07)' }}>{say}</div>
      <div style={{ padding: '6px 16px 12px' }}>
        {rows.map(([k, v], i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0',
                                 borderBottom: i < rows.length - 1 ? '1px solid #eceae2' : 'none', fontSize: 14 }}>
            <span style={{ color: '#6f6a5e' }}>{k}</span>
            <span style={{ fontWeight: 700 }}>{v}</span>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onCopy(say)}
        style={{ margin: '0 16px 14px', minHeight: 40, padding: '7px 14px', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                  cursor: 'pointer', color: '#12341f', background: 'transparent', border: '1.5px solid #e6e2d8', borderRadius: 10 }}>
        {copied === side ? 'Copied ✓' : 'Copy result'}
      </button>
    </div>
  );
}

export function ColdBrewDilutionView() {
  // CJ 2026-07-29: pre-fill the common case — 1 gal (128 oz) batch and the House
  // Cold Brew target of 1.6% TDS in both modes — so the station usually only
  // enters the refractometer reading.
  const [aConc, setAConc] = useState(''); const [aCtds, setACtds] = useState(''); const [aTtds, setATtds] = useState('1.6');
  const [bFinal, setBFinal] = useState('128'); const [bCtds, setBCtds] = useState(''); const [bTtds, setBTtds] = useState('1.6');
  const [aRes, setARes] = useState(null); const [bRes, setBRes] = useState(null);
  const [copied, setCopied] = useState('');

  const calcA = () => setARes(dilute({ mode: 'water', concentrateOz: aConc, concentrateTDS: aCtds, targetTDS: aTtds }));
  const calcB = () => setBRes(dilute({ mode: 'concentrate', finalVolume: bFinal, concentrateTDS: bCtds, targetTDS: bTtds }));

  async function copy(side, text) {
    try { await navigator.clipboard.writeText(text); setCopied(side); setTimeout(() => setCopied(''), 1400); } catch { /* clipboard blocked */ }
  }

  const cardStyle = { background: '#fff', border: '1px solid #e6e2d8', borderRadius: 18, padding: '20px 20px 22px',
                       boxShadow: '0 1px 2px rgba(20,16,8,.05), 0 8px 24px rgba(20,16,8,.06)' };
  const tagStyle = { alignSelf: 'flex-start', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                      color: '#12341f', background: 'rgba(31,77,58,0.14)', padding: '4px 10px', borderRadius: 999 };
  const btnStyle = { minHeight: 52, width: '100%', border: 'none', borderRadius: 12, cursor: 'pointer', background: FOREST,
                      color: '#fff', fontSize: 16, fontWeight: 700, fontFamily: 'inherit', marginTop: 4 };

  return (
    <div style={{ fontVariantNumeric: 'tabular-nums' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: FOREST }}>Prep tools</div>
        <h2 style={{ fontSize: 26, fontWeight: 800, margin: '6px 0 4px', letterSpacing: '-0.02em' }}>Cold Brew Dilution</h2>
        <div style={{ color: '#6f6a5e', fontSize: 14, maxWidth: '60ch' }}>
          Read the concentrate TDS off the refractometer, enter your numbers, and get the exact pour — no math at the station. Filtered water is treated as 0% TDS.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>
        {/* Mode B — batch-size first (CJ 2026-07-29): the common station task is
            "make 1 gal", so it leads. */}
        <section style={{ ...cardStyle, display: 'flex', flexDirection: 'column' }}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); calcB(); } }}>
          <span style={tagStyle}>I need a set amount</span>
          <h3 style={{ fontSize: 18, margin: '12px 0 2px' }}>Pull concentrate for a target batch size</h3>
          <div style={{ color: '#6f6a5e', fontSize: 13.5, marginBottom: 16 }}>How much concentrate and water to make a full urn or jug.</div>
          <Field label="Final batch size" value={bFinal} onChange={setBFinal} unit="oz" placeholder="128" onEnter={calcB} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            <Chip onClick={() => setBFinal('128')}>1 gal</Chip>
            <Chip onClick={() => setBFinal('384')}>3 gal</Chip>
            <Chip onClick={() => setBFinal('640')}>5 gal</Chip>
          </div>
          <Field label="Concentrate TDS (from refractometer)" value={bCtds} onChange={setBCtds} unit="%" placeholder="4.0" onEnter={calcB} />
          <Field label="Target TDS" value={bTtds} onChange={setBTtds} unit="%" placeholder="1.6" onEnter={calcB} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            <Chip preset onClick={() => setBTtds('1.6')}>House Cold Brew · 1.6%</Chip>
          </div>
          <button type="button" style={btnStyle} onClick={calcB}>Calculate concentrate</button>
          <Result side="b" res={bRes} copied={copied} onCopy={(t) => copy('b', t)} />
        </section>

        {/* Mode A */}
        <section style={{ ...cardStyle, display: 'flex', flexDirection: 'column' }}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); calcA(); } }}>
          <span style={tagStyle}>I have concentrate</span>
          <h3 style={{ fontSize: 18, margin: '12px 0 2px' }}>Add water to a batch of concentrate</h3>
          <div style={{ color: '#6f6a5e', fontSize: 13.5, marginBottom: 16 }}>How much water to add to hit your target strength.</div>
          <Field label="Concentrate on hand" value={aConc} onChange={setAConc} unit="oz" placeholder="32" onEnter={calcA} />
          <Field label="Concentrate TDS (from refractometer)" value={aCtds} onChange={setACtds} unit="%" placeholder="4.0" onEnter={calcA} />
          <Field label="Target TDS" value={aTtds} onChange={setATtds} unit="%" placeholder="1.6" onEnter={calcA} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            <Chip preset onClick={() => setATtds('1.6')}>House Cold Brew · 1.6%</Chip>
          </div>
          <button type="button" style={btnStyle} onClick={calcA}>Calculate water</button>
          <Result side="a" res={aRes} copied={copied} onCopy={(t) => copy('a', t)} />
        </section>
      </div>

      <div style={{ marginTop: 24, color: '#6f6a5e', fontSize: 12.5, lineHeight: 1.6 }}>
        Both modes use the same relationship — concentrate strength × its volume equals final strength × final volume — solved for the missing number.
        Press Enter in any field to calculate. Volumes round to one decimal. This tool dilutes; read TDS from the refractometer.
      </div>
    </div>
  );
}
